import Fuse from 'fuse.js';

export interface SearchShortcut {
  id: number;
  name: string;
  description: string;
  icon: string;
  url: string;
  searchTerms: string[]; // Index of search terms that should match this shortcut
}

export const SEARCH_SHORTCUTS: SearchShortcut[] = [
  {
    id: 1,
    name: 'Create a Hike',
    description: 'Plot a custom route on the map',
    icon: 'pin_drop',
    url: '/hike-planner',
    searchTerms: [
      'create',
      'create a hike',
      'create route',
      'plot',
      'plot route',
      'plan',
      'plan route',
      'plan hike',
      'planner',
      'hike planner',
      'route planner',
      'make route',
      'build route',
      'design route',
      'custom route',
      'new route',
      'new hike'
    ]
  },
  {
    id: 2,
    name: 'Search the hike map',
    description: 'Find over 120,000+ hikes on the map',
    icon: 'map',
    url: '/explore',
    searchTerms: [
      'explore',
      'search map',
      'hike map',
      'map',
      'find hikes',
      'find routes',
      'find trails',
      'search hikes',
      'search routes',
      'search trails',
      'browse',
      'browse hikes',
      'discover',
      'discover hikes',
      'look for hikes',
      'find a route',
      'find a hike',
      'find a trail'
    ]
  },
  {
    id: 3,
    name: 'View my hikes',
    description: 'All of your custom and saved hikes',
    icon: 'place',
    url: '/user/hikes',
    searchTerms: [
      'my hikes',
      'my routes',
      'my trails',
      'saved hikes',
      'saved routes',
      'saved trails',
      'my saved hikes',
      'my saved routes',
      'my saved trails',
      'custom hikes',
      'custom routes',
      'my custom hikes',
      'my custom routes',
      'bookmarks',
      'bookmarked hikes',
      'my bookmarks',
      'favourites',
      'favorites',
      'my favourites',
      'my favorites'
    ]
  },
  {
    id: 4,
    name: 'View my recordings',
    description: 'All of your tracked hikes',
    icon: 'radio_button_checked',
    url: '/user/recordings',
    searchTerms: [
      'my recordings',
      'recordings',
      'tracked hikes',
      'my tracked hikes',
      'gps recordings',
      'my gps',
      'tracked routes',
      'my tracks',
      'tracks',
      'activity',
      'my activity',
      'activities',
      'my activities',
      'history',
      'hike history',
      'past hikes',
      'completed hikes'
    ]
  },
  {
    id: 5,
    name: 'View my Print Maps',
    description: 'All of your printable maps',
    icon: 'print',
    url: '/user/print-maps',
    searchTerms: [
      'print',
      'print maps',
      'printable maps',
      'my print maps',
      'my maps',
      'download maps',
      'pdf maps',
      'offline maps',
      'paper maps',
      'print route',
      'print hike',
      'printable',
      'export map',
      'map pdf'
    ]
  },
  {
    id: 6,
    name: 'Account settings',
    description: 'Manage your account',
    icon: 'settings',
    url: '/user/settings',
    searchTerms: [
      'settings',
      'account',
      'account settings',
      'preferences',
      'my account',
      'profile settings',
      'user settings',
      'configuration',
      'config',
      'manage account',
      'edit account',
      'update profile',
      'change password',
      'privacy',
      'notifications'
    ]
  },
  {
    id: 7,
    name: 'Help Center',
    description: 'Get support and tips',
    icon: 'help',
    url: 'https://support.hiiker.app/',
    searchTerms: [
      'help',
      'help center',
      'support',
      'how to',
      'tutorial',
      'guide',
      'faq',
      'questions',
      'assistance',
      'get help',
      'need help',
      'tips',
      'learn',
      'documentation',
      'docs',
      'contact',
      'contact support'
    ]
  }
];

// Configure Fuse.js instance for searching shortcuts
const fuse = new Fuse(SEARCH_SHORTCUTS, {
  keys: [
    { name: 'name', weight: 2 },
    { name: 'searchTerms', weight: 3 },
    { name: 'description', weight: 1 }
  ],
  threshold: 0.4,
  includeScore: true,
  ignoreLocation: true,
  useExtendedSearch: false,
  minMatchCharLength: 1
});

/**
 * Search shortcuts by query string using Fuse.js fuzzy matching
 * @param query - The search query
 * @returns Array of matching shortcuts, sorted by relevance
 */
export function searchShortcuts(query: string): SearchShortcut[] {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const results = fuse.search(query.trim());
  return results.map(result => result.item);
}
